import { Extension } from '@tiptap/core'
import { Node as ProsemirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import LinterPlugin, { Result } from './plugin'
// import { Punctuation } from './rules/punctuation'
// import { HeadingLevel } from './rules/heading-level'
import { AllowedFormatting } from './rules/allowed-formatting'

export function getIssueProps(issue: Result) {
  if (issue.type === 'node') {
    return {
      class: `pm-linter-problem pm-linter-level-${issue.level} pm-linter-type-node mb-4 relative cursor-default not-prose`,
      'data-issue-from': String(issue.from),
      'data-issue-to': String(issue.to),
      'data-issue-level': issue.level,
      'data-issue-message': issue.message,
    }
  } else {
    return {
      class: `pm-linter-problem pm-linter-level-${issue.level} pm-linter-type-inline`,
      'data-issue-from': String(issue.from),
      'data-issue-to': String(issue.to),
      'data-issue-level': issue.level,
      'data-issue-message': issue.message,
    }
  }
}

function runAllLinterPlugins(
  doc: ProsemirrorNode,
  plugins: Array<typeof LinterPlugin>,
  props: unknown
) {
  const decorations: [any?] = []

  const results = plugins
    .map((RegisteredLinterPlugin) => {
      return new RegisteredLinterPlugin(doc, props).scan().getResults()
    })
    .flat()

  results.forEach((issue) => {
    if (issue.type === 'node') {
      decorations.push(
        Decoration.node(issue.from, issue.to, getIssueProps(issue))
      )
    } else {
      decorations.push(
        Decoration.inline(issue.from, issue.to, getIssueProps(issue))
      )
    }
  })

  return DecorationSet.create(doc, decorations)
}

export interface LinterOptions {
  plugins: Array<typeof LinterPlugin>
  allowedFormatting: string[] | null
}

export const Root = Extension.create<LinterOptions>({
  name: 'linter',
  addOptions() {
    return {
      plugins: [],
      allowedFormatting: null,
    }
  },
  addProseMirrorPlugins() {
    const { plugins, allowedFormatting: initialAllowedFormatting } =
      this.options

    this.editor.storage.linter = {
      allowedFormatting: initialAllowedFormatting,
    }
    const getAllowedFormatting = () => {
      return this.editor.storage.linter.allowedFormatting
    }
    const setAllowedFormatting = (v: string[]) => {
      this.editor.storage.linter.allowedFormatting = v
    }

    return [
      new Plugin({
        key: new PluginKey('linter'),
        state: {
          init(_, { doc }) {
            return runAllLinterPlugins(doc, plugins, {
              allowedFormatting: initialAllowedFormatting,
            })
          },
          apply(transaction) {
            const allowedFormatting =
              transaction.getMeta('allowedFormatting') ?? getAllowedFormatting()

            setAllowedFormatting(allowedFormatting)

            return runAllLinterPlugins(transaction.doc, plugins, {
              allowedFormatting,
            })
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
        },
      }),
    ]
  },
})

export type LinterExtensionOptions = {
  allowedFormatting: string[] | null
}

export const getLinterExtension = ({
  allowedFormatting,
}: LinterExtensionOptions) => {
  return Root.configure({
    plugins: [
      // Punctuation,
      // HeadingLevel,
      AllowedFormatting,
    ],
    allowedFormatting,
  })
}
